'use client';

import React from 'react';
import { useId } from 'react-aria';
import clsx from 'clsx';
import { Box } from 'components/Box';
import { Text } from 'components/Text';
import { theme } from '@frameio/vapor-theme';
import styles from './Spinner.module.css';

type SpinnerSizes = 'xx-small' | 'x-small' | 'small' | 'medium' | 'large';

type SpinnerVariants = 'default' | 'brand' | 'brandMulti' | 'brandOverlay';

type AccessibleLabelProps =
  | { label: string; 'aria-label'?: never; 'aria-labelledby'?: never }
  | { label?: never; 'aria-label': string; 'aria-labelledby'?: never }
  | { label?: never; 'aria-label'?: never; 'aria-labelledby': string };

export type SpinnerProps = {
  /** Whether or not to display the spinner. */
  isLoading: boolean;
  /** Delay (in ms) from when isLoading is true, and the spinner fades in. */
  delay?: number;
  /** Duration for the enter animation in milliseconds */
  enterDuration?: number;
  /** @deprecated exit animations are now disabled. This props will have no
   * effect. */
  exitDuration?: number;
  /** Size of the spinner. */
  size?: SpinnerSizes;
  /**
   * Centers itself within a relative container using absolute positioning. If a number is
   * provided, it centers and uses that number as a y offset.
   */
  centered?: boolean | number;
  /** All spinners using this prop will be synchronized, regardless of when they were first mounted. */
  syncIndeterminant?: boolean;
  /** The color variant of the spinner. */
  variant?: SpinnerVariants;
  /** Ref to the spinner container element. */
  ref?: React.Ref<HTMLDivElement>;
} & React.HTMLAttributes<HTMLDivElement> &
  AccessibleLabelProps;

/**
 * A spinner component that can be used to indicate loading or processing.
 *
 * @example
 * ```jsx
 * <Spinner
 *  isLoading={isLoading}
 *  aria-label="Loading"
 *  size="medium"
 *  delay={0}
 *  centered
 * />
 * ```
 */

export function Spinner({ isLoading, ...props }: SpinnerProps) {
  if (!isLoading) return null;
  return <SpinnerSvg {...props} isLoading={true} />;
}

function SpinnerSvg({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  label,
  centered,
  delay = 1000,
  enterDuration = 720,
  isLoading,
  size = 'medium',
  variant = 'default',
  style,
  className,
  ref,
  ...other
}: SpinnerProps) {
  const [circleSize, strokeWidth] = getSizeAndStrokeWidth(size);

  const gradientId = useId();
  const maskId = useId();

  const circleDimensions: React.SVGProps<SVGCircleElement> = {
    cx: '50%',
    cy: '50%',
    r: `${circleSize / 2 - strokeWidth / 2}px`,
    strokeWidth,
  };

  const ringAttributes: React.SVGProps<SVGCircleElement> = {
    ...circleDimensions,
    pathLength: 100,
    strokeLinecap: 'round',
    fill: 'none',
    strokeDasharray: '100 200',
    transform: 'rotate(-45)',
  };

  const spinnerColors = theme.colors.components.spinner[variant];

  return (
    <div
      ref={ref}
      aria-busy={isLoading}
      aria-label={ariaLabel || label}
      aria-labelledby={ariaLabelledBy}
      aria-live="polite"
      className={clsx(styles.spinner, className)}
      data-centered={centered || undefined}
      data-chromatic="ignore"
      role="progressbar"
      style={{
        ...style,
        animationDelay: `${delay}ms`,
        animationDuration: `${enterDuration}ms`,
        width: circleSize,
        height: circleSize,
      }}
      {...other}
    >
      <svg
        className={styles.rings}
        fill="none"
        height={circleSize}
        width={circleSize}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#000" />
            <stop offset="100%" stopColor="#fff" />
          </linearGradient>
          <mask id={maskId}>
            <rect fill={`url(#${gradientId})`} height="100%" width="100%" />
          </mask>
        </defs>
        <circle
          {...circleDimensions}
          stroke={spinnerColors.trackColor}
          strokeWidth={strokeWidth}
        />
        <g>
          <circle
            className={styles.ringStroke}
            stroke={spinnerColors.stopColorEnd}
            {...ringAttributes}
          />
        </g>
        <g mask={`url(#${maskId})`}>
          <circle
            className={styles.ringStroke}
            opacity="0.9"
            stroke={spinnerColors.stopColorBegin}
            {...ringAttributes}
          />
        </g>
      </svg>
      {label && (
        <Box
          left="50%"
          position="absolute"
          top="calc(100% + 8px)"
          transform="translateX(-50%)"
        >
          <Text color="tertiary" textAlign="center" variant="body">
            {label}
          </Text>
        </Box>
      )}
    </div>
  );
}

function getSizeAndStrokeWidth(size: SpinnerSizes) {
  switch (size) {
    case 'large':
      return [64, 6];
    case 'medium':
      return [32, 3];
    case 'small':
      return [24, 2];
    case 'x-small':
      return [14, 2];
    case 'xx-small':
      return [12, 2];
    default:
      return [24, 4];
  }
}
